import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
/**
 * Componentes
 */
import { PageHelper } from 'Components/PageHelper';
import { DefaultTable } from 'Components/DefaultTable';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { buildTableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { deleteData, getDatasPaged } from 'services/datas-service';
import { PageLoaderList } from 'Components/PageLoader/List';

import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';

import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import { Pagination, Stack } from '@mui/material';

export const Data = () => {
  const { t } = useTranslation(['mapping', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const tableHeader = useMemo(() => buildTableHeader(t), [t]);
  const tourSteps = useMemo(
    () => [
      {
        target: 'body',
        content: t('mapping:dataTypes.tour.list.welcome'),
        disableBeacon: true,
        disableScrolling: true,
        placement: 'center',
      },
      {
        target: '[data-tour="page-title"] button',
        content: t('mapping:dataTypes.tour.list.addButton'),
        disableScrolling: true,
      },
      {
        target: '[data-tour="info-card"]',
        content: t('mapping:dataTypes.tour.list.infoCard'),
        disableScrolling: true,
      },
      {
        target: '[data-tour="table"]',
        content: t('mapping:dataTypes.tour.list.table'),
        disableScrolling: false,
      },
      {
        target: '[data-tour="pagination"]',
        content: t('mapping:dataTypes.tour.list.pagination'),
        disableScrolling: false,
      },
      {
        target: 'body',
        content: t('mapping:dataTypes.tour.list.complete'),
        disableScrolling: true,
        placement: 'center',
      },
    ],
    [t]
  );
  const {
    isLoading,
    error,
    data: pagedDatas,
  } = useQuery(['getData', reFetch, page], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getDatasPaged(selectedTenant.slug, page, 15);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteData(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!reFetch);
        SwalReact.fire({
          title: t('mapping:dataTypes.notifications.deleteSuccessTitle'),
          text: t('mapping:dataTypes.notifications.deleteSuccessText'),
          icon: 'success',
        });
      } else {
        throw new Error(t('mapping:dataTypes.notifications.deleteError'));
      }
    } catch (error) {
      SwalReact.fire({
        title: t('mapping:dataTypes.notifications.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <Container title={t('mapping:dataTypes.list.title')} breadcrumb={t('mapping:dataTypes.list.breadcrumb')} showButtonAdd linkPage="/data" data-tour="page-title">
      <div data-tour="info-card">
      <Information
        title={t('mapping:dataTypes.list.cardTitle')}
        description={t('mapping:dataTypes.list.cardDescription')}
        style={{
          marginTop: '24px',
        }}
      />
      </div>
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('mapping:dataTypes.list.genericError')}</div>}
      {!isLoading && !error && pagedDatas.data.length === 0 && (
        <NoData description={t('mapping:dataTypes.list.empty')} />
      )}
      {!isLoading && !error && pagedDatas.data.length > 0 && (
        <div className="container">
          <div data-tour="table">
          <DefaultTable rows={tableHeader} cells={pagedDatas.data} onClickDelete={onDelete} link="data" />
          </div>
          <div data-tour="pagination">
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
            <Pagination
              count={pagedDatas.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          </div>
          <ModalConfirmDelete
            description={
              <span
                dangerouslySetInnerHTML={{
                  __html: t('mapping:dataTypes.list.deleteConfirm', { name: itemToDelete?.name ?? '' }),
                }}
              />
            }
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="data-list" />
    </Container>
  );
};
